import { Component, OnInit } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { MatSnackBar } from '@angular/material';

const DB_PATH = '/meta/website/localities';

interface Locality {
  _index: number;
  cityName: string;
  displayCity: string;
  cityslug: string;
  cityPriority: string;
  localityName: string;
  localitySlug: string;
  localitySort: string;
  _editing?: boolean;
  _saving?: boolean;
  _draft?: Partial<Locality>;
}

@Component({
  selector: 'app-config-website-localities',
  templateUrl: './config-website-localities.component.html',
  styleUrls: ['./config-website-localities.component.scss']
})
export class ConfigWebsiteLocalitiesComponent implements OnInit {
  all: Locality[] = [];
  filtered: Locality[] = [];
  loading = true;
  quickSearch = '';
  filterCity = 'All';
  cities: string[] = [];

  showAddPanel = false;
  adding = false;
  newLocality: Partial<Locality> = this.empty();

  constructor(private db: AngularFireDatabase, private snack: MatSnackBar) {}

  ngOnInit() {
    this.db.database.ref(DB_PATH).once('value', snapshot => {
      const val = snapshot.val();
      if (Array.isArray(val)) {
        this.all = val
          .filter(v => v != null)
          .map((v, i) => ({
            _index: i,
            cityName: v.cityName || '',
            displayCity: v.displayCity || '',
            cityslug: v.cityslug || '',
            cityPriority: String(v.cityPriority || ''),
            localityName: v.localityName || '',
            localitySlug: v.localitySlug || '',
            localitySort: String(v.localitySort || ''),
          }))
          .sort((a, b) => {
            const cp = Number(a.cityPriority) - Number(b.cityPriority);
            return cp !== 0 ? cp : Number(a.localitySort) - Number(b.localitySort);
          });
        const seen = new Set<string>();
        this.cities = this.all
          .map(l => l.cityName)
          .filter(c => { if (seen.has(c)) return false; seen.add(c); return true; });
      }
      this.loading = false;
      this.applyFilter();
    });
  }

  applyFilter() {
    const q = this.quickSearch.trim().toLowerCase();
    this.filtered = this.all.filter(l => {
      const matchCity = this.filterCity === 'All' || l.cityName === this.filterCity;
      const matchQ = !q || l.localityName.toLowerCase().includes(q) || l.cityName.toLowerCase().includes(q) || l.localitySlug.toLowerCase().includes(q);
      return matchCity && matchQ;
    });
  }

  // ── Edit ─────────────────────────────────────────────────────────────────
  startEdit(l: Locality) {
    l._draft = {
      cityName: l.cityName, displayCity: l.displayCity, cityslug: l.cityslug,
      cityPriority: l.cityPriority, localityName: l.localityName,
      localitySlug: l.localitySlug, localitySort: l.localitySort,
    };
    l._editing = true;
  }

  cancelEdit(l: Locality) { l._editing = false; l._draft = undefined; }

  async saveEdit(l: Locality) {
    l._saving = true;
    const payload = {
      cityName: l._draft.cityName, displayCity: l._draft.displayCity,
      cityslug: l._draft.cityslug, cityPriority: l._draft.cityPriority,
      localityName: l._draft.localityName, localitySlug: l._draft.localitySlug,
      localitySort: l._draft.localitySort,
    };
    try {
      await this.db.database.ref(`${DB_PATH}/${l._index}`).update(payload);
      Object.assign(l, payload);
      l._editing = false; l._draft = undefined;
      this.snack.open(`${l.localityName} updated`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Save failed. Please try again.', 'OK', { duration: 3000 });
    }
    l._saving = false;
  }

  // ── Add ──────────────────────────────────────────────────────────────────
  openAddPanel() { this.newLocality = this.empty(); this.showAddPanel = true; }
  cancelAdd() { this.showAddPanel = false; }

  async addLocality() {
    if (!this.newLocality.cityName || !this.newLocality.localityName || !this.newLocality.localitySlug) {
      this.snack.open('City name, Locality name and Slug are required.', 'OK', { duration: 2500 });
      return;
    }
    this.adding = true;
    const nextIndex = this.all.length > 0 ? Math.max(...this.all.map(l => l._index)) + 1 : 0;
    const payload = {
      cityName: this.newLocality.cityName,
      displayCity: this.newLocality.displayCity || this.newLocality.cityName,
      cityslug: (this.newLocality.cityslug || this.newLocality.cityName).toLowerCase().replace(/\s+/g, '-'),
      cityPriority: String(this.newLocality.cityPriority || ''),
      localityName: this.newLocality.localityName,
      localitySlug: (this.newLocality.localitySlug || '').toLowerCase().replace(/\s+/g, '-'),
      localitySort: String(this.newLocality.localitySort || ''),
    };
    try {
      await this.db.database.ref(`${DB_PATH}/${nextIndex}`).set(payload);
      const entry: Locality = { _index: nextIndex, ...payload };
      this.all.push(entry);
      this.all.sort((a, b) => {
        const cp = Number(a.cityPriority) - Number(b.cityPriority);
        return cp !== 0 ? cp : Number(a.localitySort) - Number(b.localitySort);
      });
      if (!this.cities.includes(payload.cityName)) this.cities.push(payload.cityName);
      this.applyFilter();
      this.showAddPanel = false;
      this.snack.open(`${entry.localityName} added`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Add failed. Please try again.', 'OK', { duration: 3000 });
    }
    this.adding = false;
  }

  private empty(): Partial<Locality> {
    return { cityName: '', displayCity: '', cityslug: '', cityPriority: '', localityName: '', localitySlug: '', localitySort: '' };
  }
}
